import type { HintView, QuestionView } from "../api/types.js";
import { answerTypeLabel } from "../lib/format.js";
import { AnswerForm, type AnswerSubmission } from "./AnswerForm.js";
import { HintList } from "./HintList.js";

interface Props {
  question: QuestionView;
  expanded: boolean;
  submitting: boolean;
  openingHintId: string | null;
  cooldownSec: number;
  /** イベントが受付停止・終了している */
  locked: boolean;
  onToggle: () => void;
  onSubmit: (submission: AnswerSubmission) => void;
  onRequestHint: (hint: HintView) => void;
}

export function QuestionCard({
  question,
  expanded,
  submitting,
  openingHintId,
  cooldownSec,
  locked,
  onToggle,
  onSubmit,
  onRequestHint,
}: Props): React.ReactElement {
  // サーバーが数えた残り回数をそのまま使う。
  // 画面側で maxAttempts から引くと、チーム単位の上限のときに
  // 自分の解答数しか引けず、実際より多く見えてしまう。
  const attemptsLeft = question.remainingAttempts;
  const outOfAttempts = attemptsLeft !== null && attemptsLeft <= 0;

  /*
   * 残り回数の文言はスコープで分ける。
   * チーム単位の上限で「残り1回」とだけ出すと、まだ自分では解答していない人が
   * 「自分にあと1回ある」と読んでしまい、メンバーが使い切った回数と取り違える。
   */
  const isTeamScope = question.attemptScope === "team";
  const attemptsLabel = isTeamScope ? "チームの残り解答回数" : "あなたの残り解答回数";

  /*
   * 解放済みの大謎（未解放のものはそもそもここへ来ない）。
   * 最後の1問だと分かるように印を付ける。参加者向けの呼び方は「最終謎」。
   */
  const isFinal = question.kind === "final";

  return (
    <section
      className={`card ${question.solved ? "card--solved" : ""} ${isFinal ? "card--final" : ""}`}
    >
      <button
        type="button"
        className="card__head"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className="card__head-main">
          <span className="card__label">
            {isFinal && <span className="card__final-mark">最終謎</span>}
            {question.label}
          </span>
          <span className="card__meta">
            {question.points}点
            {question.penaltyWrong > 0 && ` ・ 誤答 -${question.penaltyWrong}点`}
            {` ・ ${answerTypeLabel(question.answerType)}`}
          </span>
        </div>
        <span className={`badge ${question.solved ? "badge--solved" : "badge--open"}`}>
          {question.solved ? "正解" : "未解答"}
        </span>
      </button>

      {expanded && (
        <div className="card__body">
          {question.solved ? (
            <p className="card__solved-note">
              この問題は正解済みです。
              {question.hints.some((h) => h.opened) && " 使用したヒントは下に表示されます。"}
            </p>
          ) : outOfAttempts ? (
            <p className="card__blocked-note">
              {isTeamScope
                ? "この問題はチームの解答回数の上限に達しました。"
                : "解答回数の上限に達しました。"}
            </p>
          ) : locked ? (
            <p className="card__blocked-note">現在、解答の受付を停止しています。</p>
          ) : (
            <>
              {attemptsLeft !== null && (
                <p
                  className={`card__attempts ${attemptsLeft <= 1 ? "card__attempts--last" : ""}`}
                >
                  {attemptsLabel}：{attemptsLeft}回
                  {attemptsLeft <= 1 && "（これが最後の1回です）"}
                </p>
              )}
              <AnswerForm
                question={question}
                submitting={submitting}
                cooldownSec={cooldownSec}
                disabled={locked}
                onSubmit={onSubmit}
              />
            </>
          )}

          <HintList
            hints={question.hints}
            disabled={locked || question.solved}
            openingHintId={openingHintId}
            onRequestOpen={onRequestHint}
          />
        </div>
      )}
    </section>
  );
}
